import React from "react";
import { formatNumberToLocal, toastError } from "../../helpers";
import { Button, Card, Image, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import productImg from "../../assets/images/product-image.svg";
import { useNavigate, useParams } from "react-router";
import { useDispatch } from "react-redux";
import { setProductData } from "../../app/features/ProductSlice";
import { BANK_PAYMENT_METHODS, CURRENCY_SYMBOLS } from "../../Constants";

const HOnePurchaseSummary = ({
  inputData,
  eosKey,
  currency = "CHF",
  rate = 1,
}) => {
  const currencySymbol = CURRENCY_SYMBOLS[currency] ?? currency;
  const conv = (v) => formatNumberToLocal((v ?? 0) * rate) || 0;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { planId, lang } = useParams();
  const navigate = useNavigate();

  const validateData = (data) => {
    if (
      data.ingots.reduce(
        (acc, ingot) => acc + ingot.ingot * ingot.quantity,
        0,
      ) < 100
    ) {
      toastError("", t("ingotlessThan100"));
      return false;
    }

    const mandatoryAccepted =
      data.O01 &&
      data.O02 &&
      data.O03 &&
      data.O04 &&
      data.C01 &&
      data.C02 &&
      data.C03;
    const blockchainAccepted = !data.B01 || (data.B02O && data.B03);

    if (!mandatoryAccepted || !blockchainAccepted) {
      toastError("", t("planPurchaseAcceptTNC"));
      return false;
    }

    if (!data.paymentMethod) {
      toastError("", t("paymentMethodRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    const productData = {
      ...inputData,
      planId,
      currency,
      exchangeRate: rate,
    };
    if (validateData(productData)) {
      dispatch(setProductData(productData));
      navigate(`/${lang}/helior-one/purchase-confirmation`);
    }
  };

  return (
    <Card.Body>
      <div className="d-flex">
        <Image src={productImg} className="img-fluid me-1" alt="XOne" />
        <h5 className="title">XOne</h5>
      </div>
      <hr />
      <h6 className="mb-3">{t("summary")}</h6>
      <div className="table-responsive">
        <Table className="payment-table mb-1" style={{ minWidth: "520px" }}>
          <thead>
            <tr>
              <th></th>
              <th>{t("amount")}</th>
              <th>
                {t("guaranteeMargin2")} {inputData?.guaranteeMargin}
                {"%"}
              </th>
              <th>
                {t("guaranteeMargin")}{" "}
                {inputData.paymentMethod && (
                  <>
                    (
                    {BANK_PAYMENT_METHODS.includes(inputData.paymentMethod)
                      ? inputData.txBankFee
                      : inputData.txCryptoFee}
                    %)
                  </>
                )}
              </th>
              <th>{t("total")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t("goldCost")}</td>
              <td>{conv(inputData.goldCost)}</td>
              <td>{conv(inputData.goldPriceWithGuaranteeMargin)}</td>
              <td>{conv(inputData.goldPriceWithMargin)}</td>
              <td>
                {conv(
                  inputData.goldCost +
                    inputData.goldPriceWithMargin +
                    inputData.goldPriceWithGuaranteeMargin,
                )}
              </td>
            </tr>
            <tr>
              <td>
                {t("spreadCost")}{" "}
                {inputData.spreadValue > 0 && (
                  <span>({inputData.spreadValue * 100}%)</span>
                )}
              </td>
              <td>{conv(inputData.spreadCost)}</td>
              <td>{conv(inputData.costSpreadWithGuaranteeMargin)}</td>
              <td>{conv(inputData.costSpreadWithMargin)}</td>
              <td>
                {conv(
                  inputData.spreadCost +
                    inputData.costSpreadWithMargin +
                    inputData.costSpreadWithGuaranteeMargin,
                )}
              </td>
            </tr>
            <tr>
              <td>
                {t("storageCost")}{" "}
                {inputData.storageValue > 0 && (
                  <span>({inputData.storageValue * 100}%)</span>
                )}{" "}
              </td>
              <td>{conv(inputData.storageCost)}</td>
              <td>{conv(inputData.storageCostWithGuaranteeMargin)}</td>
              <td>{conv(inputData.storageCostWithMargin)}</td>
              <td>
                {conv(
                  inputData.storageCost +
                    inputData.storageCostWithMargin +
                    inputData.storageCostWithGuaranteeMargin,
                )}
              </td>
            </tr>
            <tr>
              <td>{t("manufacturingAmount")}</td>
              <td>{conv(inputData.totalManufacturingCost)}</td>
              <td>-</td>
              <td>-</td>
              <td>{conv(inputData.totalManufacturingCost)}</td>
            </tr>
            <tr>
              <td>{t("total")}</td>
              <td>{conv(inputData.totalCost)}</td>
              <td>{conv(inputData.total_payment_with_guarantee_margin)}</td>
              <td>{conv(inputData.total_payment_with_margin)}</td>
              <td>
                {conv(
                  inputData.totalCost +
                    inputData.total_payment_with_margin +
                    inputData.total_payment_with_guarantee_margin,
                )}
              </td>
            </tr>
          </tbody>
        </Table>
      </div>
      {inputData.bank_transfer > 0 && (
        <>
          <hr />
          <p className="d-flex justify-content-between mb-1 text-uppercase flex-wrap">
            <span>{t("totalGrams")}</span> {inputData.totalGrams}gr
          </p>
          <p className="d-flex justify-content-between mb-1 text-uppercase flex-wrap">
            <span>{t("paymentMethod")}</span> {inputData.paymentMethod}
          </p>
          {inputData.stampDuty > 0 && (
            <>
              <p className="d-flex justify-content-between mb-1 text-uppercase flex-wrap">
                <span>{t("price")}</span> {currencySymbol}{" "}
                {conv(inputData.bank_transfer)}
              </p>
              <p className="d-flex justify-content-between text-uppercase flex-wrap">
                <span>{t("stampDuty")}</span> {currencySymbol}{" "}
                {conv(inputData.stampDuty)}
              </p>
            </>
          )}
          <hr />
          <p className="d-flex justify-content-between mb-5 text-uppercase flex-wrap">
            <span>{t("totalPayment")}</span> {currencySymbol}{" "}
            {conv(inputData.bank_transfer + inputData?.stampDuty)}{" "}
          </p>
          <Button
            variant="primary-outline"
            className="w-100"
            onClick={handleSubmit}
          >
            {t("buyPlan")}
          </Button>
        </>
      )}
    </Card.Body>
  );
};

export default HOnePurchaseSummary;